/* src/views/logements/ADemarcherCtrl.jsx — LOT 4 OPÉRATEUR B (2026-09-06) :
   la facette « À démarcher » de la destination ⌂ Logements. La prospection
   quitte les emails récapitulatifs pour une file in-app : l'opérateur relève
   et note les annonces, les humains les travaillent — RIEN ne part tout seul
   (sentinelle « jamais d'envoi seul », testée sur la source de ce fichier).

   Textes AU MOT PRÈS du proto docs/handoff/admin-proto.html (écrans
   opProspect / vDemarcher / feuilleAnnonce / feuillePurge) — à l'exception
   voulue des brouillons du proto (« je gère » = INTERDIT Hoguet) : les
   brouillons viennent du serveur (lib/prospection-prompts.js, formulations
   conciergerie / prise en charge, zéro chiffre) et le chip « insister sur le
   revenu » est devenu « mettre en avant le potentiel ».

   Mapping libellés SANS migration (par-dessus PROSPECT_STATUTS,
   src/lib/domain-constants.js) : pas_contacte→À regarder · contacte→Contacté ·
   discussion+proposition→En négociation · signe→Signé · perdu→Abandonné.

   ALIGNEMENTS SUR LE PROTO (contre-revue 2026-09-06 — le proto fait foi) :
   · seuil du signal « republiée » = repostCount > 1 (proto : `a.rep>1`), pas > 0 —
     une republication unique est le bruit de fond du marché ;
   · bandeauLui en tête de la feuille annonce (« C'est l'opérateur qui écrit,
     avec ce qu'il a retenu de vous — votre façon d'aborder un propriétaire ») ;
   · la RAISON de la note (raisonScore) est affichée dans le détail : une note
     sans son motif ne se conteste pas, donc ne se travaille pas. */

import { useState, useEffect, useMemo, useCallback } from 'react'
import { prospectsApi } from '../../lib/prospects-api'
import { SOURCE_LABEL_MAP } from '../../lib/domain-constants'
import { isMasque } from '../../lib/prospect-filters'
import { SERVICE_ANNONCES_EN_PAUSE, PHRASE_PAUSE } from '../../lib/service-annonces'
import { uid } from '../../lib/format'
import InteractionModal from '../../modals/InteractionModal'
import Feuille from '../accueil/Feuille'
import '../../styles/fbz-admin.css'

const displaySource = (s) => (s ? (SOURCE_LABEL_MAP[s] || s) : 'Leboncoin')

/* Les cinq états d'affichage, par-dessus les statuts stockés (0 migration). */
export const DEM_ETATS = [
  { id: 'a_regarder', lb: 'À regarder', statuts: ['pas_contacte'], cible: 'pas_contacte',
    dot: '#87b8e6', bg: 'var(--ciel-p)', tx: 'var(--navy)', bord: '#87b8e6' },
  { id: 'contacte', lb: 'Contacté', statuts: ['contacte'], cible: 'contacte',
    dot: '#d4a24c', bg: 'var(--ambre-bg)', tx: 'var(--ambre-tx)', bord: '#d4a24c' },
  { id: 'nego', lb: 'En négociation', statuts: ['discussion', 'proposition'], cible: 'discussion',
    dot: '#2f9e6e', bg: 'var(--vert-bg)', tx: 'var(--vert-tx)', bord: '#2f9e6e' },
  { id: 'signe', lb: 'Signé', statuts: ['signe'], cible: 'signe',
    dot: '#2f9e6e', bg: 'var(--vert-bg)', tx: 'var(--vert-tx)', bord: '#2f9e6e' },
  { id: 'abandonne', lb: 'Abandonné', statuts: ['perdu'], cible: 'perdu',
    dot: '#98a2ad', bg: 'var(--fond)', tx: 'var(--gris)', bord: '#98a2ad' },
]

export function etatDeProspect(p) {
  return DEM_ETATS.find((e) => e.statuts.includes(p && p.statut)) || DEM_ETATS[0]
}

/* Chips « QUI S'EN OCCUPE » : l'équipe de base + tout prénom déjà assigné. */
const EQUIPE_BASE = ['Dimitri', 'Néïsia']

/* Seuil du signal « republiée » — LE PROTO FAIT FOI : `var fort = a.rep>1`.
   Une annonce republiée UNE fois, c'est le bruit de fond du marché ; c'est à
   partir de la deuxième que « il ne trouve pas de locataire » devient une
   accroche défendable. Un seuil à 0 aurait allumé l'encart ambre sur presque
   tout le parc et l'aurait vidé de son sens. */
const SEUIL_REPUBLICATION = 1

const dateCourte = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`
}
const moisDe = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('fr-FR', { month: 'long' })
}

const titreDe = (p) => (p.type === 'agence'
  ? (p.nom || 'Agence sans nom')
  : ([p.typeLogement, p.adresse].filter(Boolean).join(' · ') || p.nom || 'Annonce'))

/* ═══ La feuille « Voir ce qu'il écrirait » (proto feuilleAnnonce) ═══ */
function FeuilleAnnonce({ prospect: p, onFermer, onCopie }) {
  const [brouillon, setBrouillon] = useState('')
  const [chargement, setChargement] = useState(true)
  const [erreur, setErreur] = useState(null)
  const [copie, setCopie] = useState(false)
  const agence = p.type === 'agence'

  useEffect(() => {
    let vivant = true
    prospectsApi.demarcherDraft(p.id)
      .then((r) => { if (vivant) { setBrouillon(r.brouillon || ''); setChargement(false) } })
      .catch((e) => {
        console.error('[demarcher] draft', e)
        if (vivant) { setErreur('La préparation du brouillon a échoué.'); setChargement(false) }
      })
    return () => { vivant = false }
  }, [p.id])

  const reecrire = async (consigne) => {
    setChargement(true)
    try {
      const r = await prospectsApi.demarcherDraft(p.id, { consigne, brouillon })
      setBrouillon(r.brouillon || brouillon)
      setErreur(null)
    } catch (e) {
      console.error('[demarcher] reecriture', e)
      setErreur('La réécriture a échoué — le brouillon est inchangé.')
    }
    setChargement(false)
  }

  const copier = async () => {
    try { await navigator.clipboard.writeText(brouillon) } catch (e) { console.error('[demarcher] clipboard', e) }
    setCopie(true)
    onCopie(p, brouillon)
  }

  return (
    <Feuille titre={titreDe(p)} testId="feuille-annonce" onFermer={onFermer} enfants={(
      <>
        {/* bandeauLui du proto — dit QUI écrit et avec quoi, avant le brouillon. */}
        <div data-testid="feuille-annonce-bandeau-lui"
          style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'var(--ciel-p)',
            border: '1px solid #c3d5e4', borderRadius: '12px', padding: '10px 12px', marginBottom: '11px' }}>
          <span aria-hidden="true" style={{ width: '26px', height: '26px', borderRadius: '50%',
            background: '#87b8e6', color: '#fff', display: 'flex', alignItems: 'center',
            justifyContent: 'center', flexShrink: 0, fontSize: '13px' }}>◈</span>
          <span style={{ flex: 1, minWidth: 0, fontSize: '11px', color: 'var(--navy-d)', lineHeight: 1.5 }}>
            C'est l'opérateur qui écrit, avec ce qu'il a retenu de vous — votre façon
            d'aborder un propriétaire.</span>
        </div>

        {!agence && (
          <div className="carte" style={{ margin: 0 }}>
            <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
              {[[p.prix ? `${p.prix} €` : '—', 'le loyer'],
                [p.surface ? `${p.surface} m²` : '—', 'la surface'],
                [`${p.score || 0}/5`, 'son intérêt']].map(([v, lb]) => (
                <span key={lb}>
                  <b style={{ display: 'block', fontSize: '16px', color: 'var(--navy-d)', lineHeight: 1.1 }}>{v}</b>
                  <span style={{ display: 'block', fontSize: '9.5px', color: 'var(--gris-d)', marginTop: '2px' }}>{lb}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {(p.repostCount || 0) > SEUIL_REPUBLICATION && (
          <div className="carte" data-testid="feuille-annonce-rep"
            style={{ margin: '9px 0 0', background: 'var(--ambre-bg)', borderColor: 'var(--ambre-bd)' }}>
            <p style={{ fontSize: '12px', fontWeight: 600, margin: 0, color: 'var(--ambre-tx)' }}>
              {`republié ${p.repostCount} fois${moisDe(p.createdAt) ? ` depuis ${moisDe(p.createdAt)}` : ''}`}</p>
            <p style={{ fontSize: '10.5px', margin: '4px 0 0', color: 'var(--ambre-tx)', opacity: 0.85, lineHeight: 1.5 }}>
              Il ne trouve pas de locataire, ou ils partent vite. C'est l'accroche à utiliser.</p>
          </div>
        )}

        <div className="carte" style={{ margin: '9px 0 0', background: '#f6f8fa' }}>
          <p style={{ fontSize: '11px', color: 'var(--gris)', margin: '0 0 6px' }}>Comment le joindre</p>
          {agence && p.email ? (
            <p style={{ fontSize: '12px', color: 'var(--navy-d)', margin: 0 }}>{p.email}</p>
          ) : (
            <p style={{ fontSize: '12px', color: 'var(--navy-d)', margin: 0 }}>
              Par la messagerie {displaySource(p.source)} — <b>il n'a pas laissé de numéro</b>, comme
              presque tous les particuliers.</p>
          )}
        </div>

        <p className="inter">Ce que je lui écrirais</p>
        <div className="carte" style={{ margin: 0 }}>
          {erreur && <p className="note" style={{ color: 'var(--terra-tx)', margin: '0 0 8px' }}>{erreur}</p>}
          <textarea value={chargement && !brouillon ? 'Il prépare le brouillon…' : brouillon}
            onChange={(e) => setBrouillon(e.target.value)}
            data-testid="feuille-annonce-brouillon"
            disabled={chargement}
            style={{ width: '100%', minHeight: '180px', border: 'none', outline: 'none', resize: 'vertical',
              fontSize: '11.5px', lineHeight: 1.7, color: 'var(--navy-d)', fontFamily: 'inherit',
              background: 'transparent', opacity: chargement ? 0.6 : 1 }} />
          <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', marginTop: '10px',
            paddingTop: '9px', borderTop: '1px solid var(--bord)' }}>
            {[['court', 'plus court'], ['chaleureux', 'plus chaleureux'], ['potentiel', 'mettre en avant le potentiel']]
              .map(([consigne, lb]) => (
                <button key={consigne} onClick={() => reecrire(consigne)} disabled={chargement}
                  style={{ minHeight: '30px', padding: '0 10px', borderRadius: '15px',
                    border: '1px solid var(--bord)', background: '#fff', fontSize: '10.5px',
                    color: 'var(--gris)', opacity: chargement ? 0.5 : 1 }}>{lb}</button>
              ))}
          </div>
        </div>

        <div className="act">
          <button className="bt bt-p" data-testid="feuille-annonce-copier"
            onClick={copier} disabled={chargement || !brouillon}>
            {copie ? 'Copié ✓' : 'Copier'}</button>
          {p.lien && (
            <button className="bt bt-s"
              onClick={() => window.open(p.lien, '_blank', 'noopener')}>Ouvrir l'annonce</button>
          )}
        </div>
        <p className="note">Copiez-le, ouvrez l'annonce, collez-le dans la messagerie. Rien ne part
          tout seul. <b>Copier le compte comme contacté</b> — vous ne réécrirez pas deux fois.</p>
      </>
    )} />
  )
}

/* ═══ La feuille de purge (proto feuillePurge) — séquence gardée ═══
   ② comptage RÉEL serveur, qui rend AUSSI les ids ; comptage KO ⇒ PAS de
   bouton, donc pas de suppression. ③ confirmation avec le NOMBRE. ④ suppression
   per-row serveur BORNÉE aux ids du ② (le parc bouge pendant que cet écran est
   affiché : sans ce mandat, on pourrait effacer plus que le nombre montré).
   ⑤ le RÉSULTAT RÉEL est affiché, puis la liste est rafraîchie à la fermeture. */
function FeuillePurge({ onFermer, onPurge }) {
  const [compte, setCompte] = useState(null)
  const [erreur, setErreur] = useState(null)
  const [enCours, setEnCours] = useState(false)
  const [resultat, setResultat] = useState(null)   // ce qui a VRAIMENT été effacé

  useEffect(() => {
    let vivant = true
    prospectsApi.demarcherCountPurge()
      .then((r) => { if (vivant) setCompte(r) })
      .catch((e) => {
        console.error('[demarcher] count purge', e)
        if (vivant) setErreur('Le comptage a échoué — rien ne sera supprimé.')
      })
    return () => { vivant = false }
  }, [])

  const confirmer = async () => {
    setEnCours(true)
    try {
      // Le mandat : exactement les annonces que l'écran vient de montrer.
      const r = await prospectsApi.demarcherPurge(compte.ids)
      setResultat(r)
    } catch (e) {
      console.error('[demarcher] purge', e)
      setErreur('La suppression a échoué.')
    }
    setEnCours(false)
  }

  const lignes = compte ? [
    ['Plus de 90 jours, jamais contactées', compte.categories?.vieilles || 0],
    ['Notées 1 ou 2 sur 5', compte.categories?.faibles || 0],
    ['Doublons de la même adresse', compte.categories?.doublons || 0],
  ] : []

  return (
    <Feuille titre="Nettoyer les pistes" testId="feuille-purge" onFermer={onFermer} enfants={(
      <>
        {/* ⑤ Le RÉSULTAT RÉEL — on ne referme pas sur un total supposé. */}
        {resultat ? (
          <div data-testid="purge-resultat">
            <p style={{ fontSize: '13px', color: 'var(--navy-d)', margin: '0 0 8px', fontWeight: 600 }}>
              {resultat.deleted} annonce{resultat.deleted > 1 ? 's' : ''} effacée{resultat.deleted > 1 ? 's' : ''}.</p>
            {resultat.ecartes > 0 && (
              <p className="note" data-testid="purge-ecartes" style={{ margin: 0 }}>
                {resultat.ecartes} annonce{resultat.ecartes > 1 ? 's ont' : ' a'} été écartée
                {resultat.ecartes > 1 ? 's' : ''} entre le comptage et la suppression : quelqu'un
                {resultat.ecartes > 1 ? ' les' : ' l\''}a reprise{resultat.ecartes > 1 ? 's' : ''} en
                main. Elles sont gardées.</p>
            )}
            <div className="act">
              <button className="bt bt-p" data-testid="purge-fermer" onClick={onPurge}>Fermer</button>
            </div>
          </div>
        ) : (
        <>
        <div className="dang">Ce geste efface définitivement des annonces collectées. Celles à qui
          vous avez écrit ne sont jamais touchées.</div>
        {erreur && <p className="note" data-testid="purge-erreur" style={{ color: 'var(--terra-tx)' }}>{erreur}</p>}
        {!compte && !erreur && <p className="note">On compte côté serveur…</p>}
        {compte && (
          <>
            <p className="inter" style={{ marginTop: 0 }}>Ce qui serait effacé</p>
            {lignes.map(([lb, n]) => (
              <div key={lb} style={{ display: 'flex', alignItems: 'center', gap: '11px',
                padding: '11px 0', borderTop: '1px solid var(--bord)' }}>
                <span style={{ flex: 1, minWidth: 0, fontSize: '12px', color: 'var(--navy-d)' }}>{lb}</span>
                <b style={{ fontSize: '11.5px', color: 'var(--gris)' }}>{n} annonce{n > 1 ? 's' : ''}</b>
              </div>
            ))}
            <p className="note">Gardées quoi qu'il arrive : celles à qui vous avez écrit, celles en
              discussion, et toutes celles notées 4 ou 5.</p>
          </>
        )}
        <div className="act">
          {compte && compte.total > 0 && (
            <button className="bt bt-p" data-testid="purge-cta"
              style={{ background: 'var(--terra)' }} disabled={enCours}
              onClick={confirmer}>{enCours ? 'Effacement…' : `Effacer les ${compte.total}`}</button>
          )}
          {compte && compte.total === 0 && <p className="note" style={{ margin: 0 }}>Rien à effacer.</p>}
          <button className="bt bt-s" onClick={onFermer}>Annuler</button>
        </div>
        </>
        )}
      </>
    )} />
  )
}

/* ═══ La vue « À démarcher » ═══ */
export default function ADemarcherCtrl({ bascule, onARegarder }) {
  const [prospects, setProspects] = useState(null) // null = pas chargé
  const [erreur, setErreur] = useState(null)
  const [filtreQui, setFiltreQui] = useState('tous')       // tous | particulier | agence
  const [filtreEtat, setFiltreEtat] = useState('tous')     // tous | id DEM_ETATS
  const [ouvertId, setOuvertId] = useState(null)
  const [feuilleId, setFeuilleId] = useState(null)         // feuille annonce
  const [purgeOuverte, setPurgeOuverte] = useState(false)
  const [noteModalId, setNoteModalId] = useState(null)     // InteractionModal (réutilisée)
  const [panneAssigne, setPanneAssigne] = useState(null)   // M3 : dire la panne, pas la taire

  const charger = useCallback(() => {
    prospectsApi.fetchProspects()
      .then((liste) => { setProspects(Array.isArray(liste) ? liste : []); setErreur(null) })
      .catch((e) => { console.error('[demarcher] fetch', e); setProspects([]); setErreur('La lecture des annonces a échoué.') })
  }, [])
  useEffect(() => { charger() }, [charger])

  const patchLocal = (row) => {
    if (!row || !row.id) return
    setProspects((prev) => (prev || []).map((x) => (x.id === row.id ? row : x)))
  }

  // La base visible : les annonces non masquées (les mortes dorment).
  const visibles = useMemo(() => (prospects || []).filter((p) => !isMasque(p)), [prospects])
  const parType = (t) => (t === 'tous' ? visibles : visibles.filter((p) => (t === 'agence' ? p.type === 'agence' : p.type !== 'agence')))
  const base = parType(filtreQui)
  const parEtat = useMemo(() => {
    const m = {}
    for (const p of base) { const e = etatDeProspect(p); m[e.id] = (m[e.id] || 0) + 1 }
    return m
  }, [base])

  const liste = base.filter((p) => filtreEtat === 'tous' || etatDeProspect(p).id === filtreEtat)

  // Compteurs du bandeau (proto : à regarder / en cours / signés).
  const nARegarder = visibles.filter((p) => etatDeProspect(p).id === 'a_regarder').length
  const nEnCours = visibles.filter((p) => ['contacte', 'nego'].includes(etatDeProspect(p).id)).length
  const nSignes = visibles.filter((p) => etatDeProspect(p).id === 'signe').length
  useEffect(() => { if (prospects !== null && onARegarder) onARegarder(nARegarder) }, [prospects, nARegarder, onARegarder])

  const nPart = parType('particulier').length
  const nAg = parType('agence').length

  const changerEtat = async (p, etat) => {
    if (etat.statuts.includes(p.statut)) return // déjà dans ce groupe — ne pas rétrograder proposition→discussion
    try {
      const maj = await prospectsApi.setStatut(p.id, etat.cible, {
        id: uid(), type: '📋 Statut', note: `→ ${etat.lb}`, date: new Date().toISOString().split('T')[0],
      })
      patchLocal(maj)
    } catch (e) { console.error('[demarcher] statut', e) }
  }

  const changerAssigne = async (p, nom) => {
    const cible = p.assigne === nom ? null : nom
    try {
      const maj = await prospectsApi.demarcherAssigne(p.id, cible)
      patchLocal(maj)
      setPanneAssigne(null)
    } catch (e) {
      console.error('[demarcher] assigne', e)
      // ⚠️ « QUI S'EN OCCUPE » S'APPUIE SUR UNE COLONNE NEUVE (`assigne`).
      // Tant que la migration du lot n'est pas appliquée, le serveur répond
      // 409 + raison 'colonne_absente'. Sans ce message, les chips restaient
      // cliquables et sans effet : l'écran mentait par omission.
      setPanneAssigne(/mise à jour de la base|colonne/i.test(e?.message || '')
        ? "« Qui s'en occupe » attend une mise à jour de la base : la note reste à faire à la main pour l'instant."
        : "L'assignation a échoué — rien n'a été enregistré.")
    }
  }

  // Copier : le compte passe « Contacté », on ne réécrit pas deux fois.
  // UN SEUL appel serveur (contact + statut dans la même mutation) — deux
  // appels enchaînés déclenchaient deux miroirs KV, donc deux relectures
  // complètes de la table pour un seul clic.
  const surCopie = async (p) => {
    try {
      const today = new Date().toISOString().split('T')[0]
      const maj = await prospectsApi.demarcherCopie(p.id, {
        dateEmailEnvoye: today,
        statutActuel: p.statut,
        interaction: { id: uid(), type: '📥 Brouillon copié', note: 'Message copié depuis « À démarcher »', date: today },
      })
      patchLocal(maj)
    } catch (e) { console.error('[demarcher] copie', e) }
  }

  const ajouterNote = async (prospectId, interaction) => {
    try {
      const maj = await prospectsApi.addInteraction(prospectId, interaction)
      patchLocal(maj)
      setNoteModalId(null)
    } catch (e) { console.error('[demarcher] note', e) }
  }

  const equipe = useMemo(() => {
    const noms = new Set(EQUIPE_BASE)
    for (const p of visibles) if (p.assigne) noms.add(p.assigne)
    return [...noms]
  }, [visibles])

  const feuilleProspect = feuilleId ? (prospects || []).find((p) => p.id === feuilleId) : null

  return (
    <>
      {/* Le bandeau du proto vDemarcher : sous-titre + 3 compteurs. */}
      <div className="bandeau" data-testid="demarcher-bandeau">
        <p className="bSous">Les biens que vous pourriez ajouter à votre parc</p>
        <h2 className="bTitre">Logements</h2>
        <div style={{ display: 'flex', gap: '8px', marginTop: '12px', paddingTop: '11px',
          borderTop: '1px solid rgba(255,255,255,.18)' }}>
          {[[nARegarder, 'à regarder'], [nEnCours, 'en cours'], [nSignes, 'signés']].map(([n, lb]) => (
            <span key={lb} style={{ flex: 1, minWidth: 0, background: 'rgba(255,255,255,.13)',
              borderRadius: '11px', padding: '8px 10px' }}>
              <b style={{ display: 'block', fontSize: '17px', color: '#fff', fontWeight: 600,
                lineHeight: 1.1 }}>{n}</b>
              <span style={{ display: 'block', fontSize: '9.5px', color: 'rgba(255,255,255,.72)',
                marginTop: '2px' }}>{lb}</span>
            </span>
          ))}
        </div>
      </div>

      <div className="page">
        {bascule}
        <div className="info" data-testid="demarcher-info">L'opérateur relève les annonces chaque nuit
          et les note de 1 à 5. <b>À vous et à votre équipe de les travailler</b> — chacun voit ce que
          les autres ont fait.</div>
        {/* 01/10/2026 — abonnement au service d'annonces résilié : la file ne
            se remplit plus, on le dit en une phrase (règle 23), sans nom de
            prestataire (règle 32). Les annonces déjà en base restent listées. */}
        {SERVICE_ANNONCES_EN_PAUSE && (
          <div className="info" data-testid="demarcher-service-pause">{PHRASE_PAUSE}</div>
        )}
        {erreur && (
          <div className="info" style={{ borderColor: '#e8c9a0', background: '#fdf6ec', color: '#8a5a17' }}>
            {erreur}</div>
        )}

        {/* Qui publie : particulier ou agence. */}
        <div className="bascule" style={{ margin: '0 0 10px' }}>
          {[['tous', 'Tous', visibles.length], ['particulier', 'Particuliers', nPart], ['agence', 'Agences', nAg]]
            .map(([id, lb, n]) => (
              <button key={id} className={filtreQui === id ? 'on' : ''}
                data-testid={`demarcher-qui-${id}`} onClick={() => setFiltreQui(id)}>{lb}
                <span style={{ fontSize: '9.5px', opacity: 0.6, marginLeft: '5px' }}>{n}</span></button>
            ))}
        </div>

        {/* Le filtre par état. */}
        <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', marginBottom: '14px',
          paddingBottom: '2px', scrollbarWidth: 'none' }}>
          {[{ id: 'tous', lb: 'Tout', n: base.length, dot: null }]
            .concat(DEM_ETATS.map((e) => ({ id: e.id, lb: e.lb, n: parEtat[e.id] || 0, dot: e.dot })))
            .filter((x) => x.n > 0 || x.id === 'tous')
            .map((x) => {
              const on = filtreEtat === x.id
              return (
                <button key={x.id} onClick={() => setFiltreEtat(x.id)}
                  data-testid={`demarcher-etat-${x.id}`}
                  style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: '6px',
                    minHeight: '32px', padding: '0 12px', borderRadius: '16px', fontSize: '11.5px',
                    ...(on ? { background: 'var(--navy-d)', color: '#fff', border: '1px solid var(--navy-d)', fontWeight: 600 }
                      : { background: '#fff', color: 'var(--gris)', border: '1px solid var(--bord)' }) }}>
                  {x.dot && <span style={{ width: '6px', height: '6px', borderRadius: '50%',
                    background: on ? '#fff' : x.dot, flexShrink: 0 }} />}
                  {x.lb}<span style={{ opacity: 0.6, fontSize: '10px' }}>{x.n}</span></button>
              )
            })}
        </div>

        {prospects === null && <p className="note">On lit les annonces…</p>}

        {liste.map((p) => {
          const o = ouvertId === p.id
          const E = etatDeProspect(p)
          const fort = (p.repostCount || 0) > SEUIL_REPUBLICATION && E.id === 'a_regarder'
          const derniere = (p.interactions || [])[((p.interactions || []).length - 1)]
          return (
            <div key={p.id} data-testid={`demarcher-carte-${p.id}`}
              style={{ background: '#fff', borderRadius: '15px', marginBottom: '9px', overflow: 'hidden',
                boxShadow: `0 1px 3px rgba(35,50,64,.07),0 0 0 1px ${o ? '#87b8e6' : fort ? 'var(--ambre-bd)' : 'rgba(35,50,64,.05)'}` }}>

              {/* La ligne repliée. */}
              <button onClick={() => setOuvertId(o ? null : p.id)}
                style={{ display: 'flex', alignItems: 'center', gap: '11px', width: '100%',
                  textAlign: 'left', background: o ? '#dbeaf7' : '#fff', border: 'none',
                  padding: '12px 13px', minHeight: '62px', cursor: 'pointer' }}>
                <span style={{ flexShrink: 0, width: '30px', textAlign: 'center' }}>
                  <b style={{ display: 'block', fontSize: '15px', fontWeight: 600,
                    color: (p.score || 0) >= 5 ? 'var(--vert-tx)' : 'var(--gris-d)', lineHeight: 1 }}>{p.score || 0}</b>
                  <span style={{ display: 'block', fontSize: '8px', color: 'var(--gris-d)' }}>sur 5</span></span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <b style={{ display: 'block', fontSize: '12.5px', lineHeight: 1.35, overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{titreDe(p)}</b>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '7px', marginTop: '4px', flexWrap: 'wrap' }}>
                    <span className="tag" style={{ background: E.bg, color: E.tx, borderColor: 'transparent' }}>{E.lb}</span>
                    {p.assigne
                      ? <span style={{ fontSize: '10px', color: 'var(--gris)' }}>{p.assigne}{derniere ? ` · ${dateCourte(derniere.date)}` : ''}</span>
                      : <span style={{ fontSize: '10px', color: 'var(--gris-d)' }}>
                          {[p.surface ? `${p.surface} m²` : null, p.prix ? `${p.prix} €` : null,
                            p.type === 'agence' ? 'agence' : 'particulier'].filter(Boolean).join(' · ')}</span>}
                  </span></span>
                {fort && <span data-testid={`demarcher-dot-rep-${p.id}`} style={{ width: '7px', height: '7px',
                  borderRadius: '50%', background: 'var(--ambre)', flexShrink: 0 }} />}
                <span style={{ fontSize: '15px', color: 'var(--gris-d)', flexShrink: 0,
                  transform: `rotate(${o ? 90 : 0}deg)` }}>›</span>
              </button>

              {/* Le détail replié. */}
              {o && (
                <div style={{ padding: '13px' }}>
                  {(p.repostCount || 0) > SEUIL_REPUBLICATION && (
                    <div data-testid={`demarcher-rep-${p.id}`}
                      style={{ background: 'var(--ambre-bg)', border: '1px solid var(--ambre-bd)',
                        borderRadius: '10px', padding: '9px 11px', marginBottom: '11px' }}>
                      <p style={{ fontSize: '11px', color: 'var(--ambre-tx)', fontWeight: 600, margin: 0 }}>
                        {`republié ${p.repostCount} fois${moisDe(p.createdAt) ? ` depuis ${moisDe(p.createdAt)}` : ''}`}</p>
                      <p style={{ fontSize: '10.5px', margin: '4px 0 0', color: 'var(--ambre-tx)',
                        opacity: 0.85, lineHeight: 1.5 }}>Il ne trouve pas de locataire, ou ils partent
                        vite. C'est l'accroche à utiliser.</p>
                    </div>
                  )}
                  {p.raisonScore && (
                    <p data-testid={`demarcher-raison-${p.id}`}
                      style={{ fontSize: '11px', color: 'var(--gris)', margin: '0 0 9px', lineHeight: 1.5 }}>
                      <b style={{ color: 'var(--navy-d)' }}>{p.score || 0} sur 5</b> — {p.raisonScore}</p>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '11px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '11px', color: 'var(--gris)' }}>
                      {[p.surface ? `${p.surface} m²` : null, p.prix ? `${p.prix} €` : null,
                        p.type === 'agence' ? 'agence' : 'particulier'].filter(Boolean).join(' · ')}</span>
                    <span style={{ fontSize: '10px', color: 'var(--gris-d)', marginLeft: 'auto' }}>
                      {displaySource(p.source)}{p.lastSeenAt ? ` · vue le ${dateCourte(p.lastSeenAt)}` : ''}</span>
                  </div>

                  {derniere && (
                    <div style={{ background: 'var(--fond)', borderRadius: '11px', padding: '10px 12px', marginBottom: '12px' }}>
                      <span style={{ display: 'block', fontSize: '9.5px', color: 'var(--gris-d)',
                        letterSpacing: '.04em', marginBottom: '5px' }}>DERNIER ÉCHANGE</span>
                      <p style={{ fontSize: '11.5px', color: 'var(--navy-d)', margin: 0, lineHeight: 1.55 }}>
                        {derniere.note || derniere.type}</p>
                      <p style={{ fontSize: '10px', color: 'var(--gris-d)', margin: '6px 0 0' }}>
                        {[p.assigne ? `par ${p.assigne}` : null, dateCourte(derniere.date)].filter(Boolean).join(' · ')}</p>
                    </div>
                  )}

                  <span style={{ display: 'block', fontSize: '9.5px', color: 'var(--gris-d)',
                    letterSpacing: '.04em', marginBottom: '8px' }}>OÙ ON EN EST</span>
                  <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', marginBottom: '12px' }}>
                    {DEM_ETATS.map((e) => {
                      const on = e.statuts.includes(p.statut)
                      return (
                        <button key={e.id} onClick={() => changerEtat(p, e)}
                          data-testid={`demarcher-set-${e.id}-${p.id}`}
                          style={{ flex: 1, minWidth: 'calc(33% - 4px)', minHeight: '36px',
                            borderRadius: '9px', fontSize: '10.5px', lineHeight: 1.25, padding: '0 6px',
                            ...(on ? { background: e.bg, color: e.tx, border: `1.5px solid ${e.bord}`, fontWeight: 600 }
                              : { background: '#fff', color: 'var(--gris)', border: '1px solid var(--bord)' }) }}>
                          {e.lb}</button>
                      )
                    })}
                  </div>

                  <span style={{ display: 'block', fontSize: '9.5px', color: 'var(--gris-d)',
                    letterSpacing: '.04em', marginBottom: '8px' }}>QUI S'EN OCCUPE</span>
                  <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
                    {equipe.map((nom) => {
                      const on = p.assigne === nom
                      const initiales = nom.slice(0, 2).toUpperCase()
                      return (
                        <button key={nom} onClick={() => changerAssigne(p, nom)}
                          data-testid={`demarcher-assigne-${nom}-${p.id}`}
                          style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                            gap: '6px', minHeight: '38px', borderRadius: '9px', fontSize: '11px',
                            ...(on ? { background: 'var(--ciel-p)', color: 'var(--navy)', border: '1.5px solid #87b8e6', fontWeight: 600 }
                              : { background: '#fff', color: 'var(--gris)', border: '1px solid var(--bord)' }) }}>
                          <span style={{ width: '20px', height: '20px', borderRadius: '50%',
                            background: on ? '#87b8e6' : '#eef2f6', color: on ? '#fff' : 'var(--gris-d)',
                            fontSize: '8.5px', fontWeight: 600, display: 'flex', alignItems: 'center',
                            justifyContent: 'center', flexShrink: 0 }}>{initiales}</span>
                          {nom}</button>
                      )
                    })}
                  </div>

                  {panneAssigne && (
                    <p className="note" data-testid={`demarcher-assigne-panne-${p.id}`}
                      style={{ color: 'var(--terra-tx)', margin: '0 0 10px' }}>{panneAssigne}</p>
                  )}

                  <div className="act">
                    <button className="bt bt-n" data-testid={`demarcher-ecrirait-${p.id}`}
                      onClick={() => setFeuilleId(p.id)}>Voir ce qu'il écrirait</button>
                    <button className="bt bt-s" onClick={() => setNoteModalId(p.id)}>＋ Une note</button>
                  </div>
                </div>
              )}
            </div>
          )
        })}

        {prospects !== null && liste.length === 0 && (
          <div className="vide" style={{ margin: 0 }} data-testid="demarcher-vide">
            <div className="ic">◈</div>
            <p>Rien dans cet état</p>
            <small>Changez de filtre pour voir les autres</small>
          </div>
        )}

        <div className="carte" style={{ margin: '16px 0 0', background: 'var(--fond)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
            <span style={{ flex: 1, minWidth: 0 }}>
              <b style={{ display: 'block', fontSize: '11.5px' }}>Faire le ménage</b>
              <span style={{ display: 'block', fontSize: '10.5px', color: 'var(--gris)', marginTop: '2px',
                lineHeight: 1.45 }}>{(prospects || []).length} annonces collectées · les plus anciennes
                ne valent plus rien</span></span>
            <button onClick={() => setPurgeOuverte(true)} data-testid="demarcher-nettoyer"
              style={{ minHeight: '34px', padding: '0 13px', borderRadius: '9px',
                border: '1px solid var(--bord)', background: '#fff', fontSize: '11px',
                color: 'var(--gris)', flexShrink: 0 }}>Nettoyer</button>
          </div>
        </div>
        <p className="note" data-testid="demarcher-pied">L'opérateur collecte et note, il n'écrit
          jamais à votre place. Chaque message part de la main de celui qui s'en occupe.</p>
      </div>

      {feuilleProspect && (
        <FeuilleAnnonce prospect={feuilleProspect}
          onFermer={() => setFeuilleId(null)}
          onCopie={surCopie} />
      )}
      {purgeOuverte && (
        <FeuillePurge onFermer={() => setPurgeOuverte(false)}
          onPurge={() => { setPurgeOuverte(false); setProspects(null); charger() }} />
      )}
      {noteModalId && (
        <InteractionModal prospectId={noteModalId}
          onSave={(interaction) => ajouterNote(noteModalId, interaction)}
          onClose={() => setNoteModalId(null)} />
      )}
    </>
  )
}
